/**
 * IntakeDropZone (PROD-3204 DF-01): the drop target of the F1 patient inbox (canvas IntakeInbox, 14 Oct). Up to 100
 * PDF, JPG or PNG files for ONE patient go up as one batch. Each file then shows its own status, polled every 3 s
 * until its reading settles.
 *
 * Built here behind plain props first; the guide (§4.2) moves it into Signal DS as a block later.
 *
 * Nothing in the inbox is part of the record until a person adds it, so this component only uploads and reports.
 */
import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ProgressBar, StatusChip } from '@scripsteam/scrips-signal-ds';
import { useUploadIntakeBatch } from '../hooks/use-upload-intake-batch';
import { intakeRunStatusKey, useIntakeRunStatus } from '../hooks/use-intake-run-status';
import { IntakeApiError, retryRun } from '../api/intake-client';
import {
  checkIntakeFiles,
  INTAKE_ACCEPT_ATTR,
  refusalMessage,
  type IntakeFileRefusal,
} from '../lib/intake-file-check';
import { runStatusText } from '../lib/run-status-text';
import { DropZone } from '@/shared/ui/components/drop-zone';
import type { IntakeUploadResponse, IntakeUploadedFile } from '../types';

export interface IntakeDropZoneProps {
  patientId: string;
  /** Shown in the headline: "Drop files for <first name> here". */
  patientFirstName: string;
  /** Called once the batch is accepted by the server. */
  onUploaded?: (response: IntakeUploadResponse) => void;
  disabled?: boolean;
  /** One-shot request: while true, the file picker opens once, as if "Choose files" was clicked (chart "Add document"). */
  openPicker?: boolean;
  /** Called when the request is consumed (picker opened, or refused because disabled or busy), so it never fires later. */
  onPickerOpened?: () => void;
}

type Line =
  | { kind: 'refused'; fileName: string; code: IntakeFileRefusal }
  | { kind: 'duplicate'; fileName: string }
  | { kind: 'queued'; fileName: string; runId: string | null };

function linesFromResponse(files: IntakeUploadedFile[]): Line[] {
  return files.map((f) => {
    if (f.status === 'Refused') return { kind: 'refused', fileName: f.fileName, code: f.refusedCode ?? 'unsupported_type' };
    if (f.status === 'Duplicate' || f.duplicateOf) return { kind: 'duplicate', fileName: f.fileName };
    // `Failed` (v0.3): the file is stored but its run could not start. Its status read shows Failed, with Retry.
    return { kind: 'queued', fileName: f.fileName, runId: f.runId };
  });
}

function QueuedLine({ patientId, fileName, runId }: { patientId: string; fileName: string; runId: string | null }) {
  const queryClient = useQueryClient();
  const { data, isError } = useIntakeRunStatus(patientId, runId);
  const retry = useMutation({
    mutationFn: () => retryRun(patientId, runId as string),
    // The reply is the run back at Queued; seeding it restarts the 3 s poll.
    onSuccess: (status) => queryClient.setQueryData(intakeRunStatusKey(patientId, runId as string), status),
  });
  const { label, semantic } = isError
    ? { label: 'Status not available', semantic: 'error' as const }
    : retry.isError
      ? { label: 'Retry failed. Try again later.', semantic: 'error' as const }
      : runStatusText(data);
  return (
    <li data-slot="intake-file-line">
      <span data-slot="intake-file-name">{fileName}</span>
      <span data-slot="intake-file-state">
        <StatusChip semantic={semantic} tone="soft" size="xs">
          {label}
        </StatusChip>
        {data?.status === 'Failed' && runId ? (
          <Button size="sm" variant="secondary" loading={retry.isPending} onClick={() => retry.mutate()}>
            Retry
          </Button>
        ) : null}
      </span>
    </li>
  );
}

export function IntakeDropZone({ patientId, patientFirstName, onUploaded, disabled, openPicker, onPickerOpened }: IntakeDropZoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [busyNotice, setBusyNotice] = useState(false);
  const upload = useUploadIntakeBatch(patientId);
  const busy = upload.isPending;

  useEffect(() => {
    if (!openPicker) return;
    // Busy: the request is not queued; say so, as a drop during an upload does.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a one-shot request answered once; no render loop
    if (busy) setBusyNotice(true);
    else if (!disabled) inputRef.current?.click();
    onPickerOpened?.();
    // Runs once per request; a change of busy, disabled or the callback must not reopen the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPicker]);

  const take = (picked: File[]) => {
    if (disabled || picked.length === 0) return;
    if (busy) {
      // A drop during an upload is not queued; say so rather than let the files disappear.
      setBusyNotice(true);
      return;
    }
    setBusyNotice(false);
    const { accepted, refused } = checkIntakeFiles(picked);
    const early: Line[] = refused.map((r) => ({ kind: 'refused', fileName: r.file.name, code: r.code }));
    setLines(early);
    if (accepted.length === 0) return;
    upload.mutate(accepted, {
      onSuccess: (res) => {
        setLines([...linesFromResponse(res.files), ...early]);
        onUploaded?.(res);
      },
    });
  };

  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragging(false);
    take(Array.from(e.dataTransfer.files ?? []));
  };

  const uploadError =
    upload.error instanceof IntakeApiError && upload.error.code === 'intake_disabled'
      ? 'Document intake is not switched on for this clinic.'
      : upload.error
        ? 'The files could not be uploaded. Check the connection and try again.'
        : null;

  return (
    <section aria-label="Add files" data-slot="intake-add-files">
      {/* The design PaDropZone `bar` look (shared adapter): the brand upload mark, the title and the limits, Choose files
          at the right. Same drag / drop / pick handlers and the same hidden input. */}
      <DropZone
        look="bar"
        title={`Drop files for ${patientFirstName} here`}
        hint="Up to 100 files · PDF, JPG, PNG · lab, omics and genomic reports only. HEIC is not accepted yet."
        dragging={dragging}
        disabled={disabled || busy}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !busy) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        action={
          <Button size="sm" variant="primary" disabled={disabled || busy} loading={busy} onClick={() => inputRef.current?.click()}>
            Choose files
          </Button>
        }
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          multiple
          accept={INTAKE_ACCEPT_ATTR}
          className="sr-only"
          aria-label="Choose files"
          disabled={disabled || busy}
          onChange={(e) => {
            take(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </DropZone>

      {busy && upload.progress !== null ? (
        <ProgressBar value={Math.round(upload.progress * 100)} aria-label="Upload progress" />
      ) : null}

      {busy && busyNotice ? (
        <p role="status" data-slot="intake-quiet">
          Wait for the current upload to finish, then add more files.
        </p>
      ) : null}

      {uploadError ? (
        <p role="alert" data-slot="drop-zone-error">
          {uploadError}
        </p>
      ) : null}

      {lines.length > 0 ? (
        <ul aria-label="Files in this batch" data-slot="intake-file-lines">
          {lines.map((l, i) =>
            l.kind === 'queued' ? (
              <QueuedLine key={`${l.fileName}-${i}`} patientId={patientId} fileName={l.fileName} runId={l.runId} />
            ) : (
              <li key={`${l.fileName}-${i}`} data-slot="intake-file-line">
                <span data-slot="intake-file-name">{l.fileName}</span>
                {l.kind === 'refused' ? (
                  <span data-slot="intake-file-refused">{refusalMessage(l.code)}</span>
                ) : (
                  <StatusChip semantic="neutral" tone="soft" size="xs">
                    Already added for this patient
                  </StatusChip>
                )}
              </li>
            ),
          )}
        </ul>
      ) : null}
    </section>
  );
}
